import { ChatToggleData } from '@choco-admin/types';
import { TableRow, TableCell, Switch } from '@heroui/react';
import React, { useState } from 'react';
import { FiMessageCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  LoadingTable,
  LoadingTableColumn,
  LinkCell,
} from '../../../../../components/Table';
import { ChatListItem } from '../../../types/chatList';

interface ChatsTableProps {
  chats: readonly ChatListItem[];
  isLoading?: boolean;
  onChatToggle: (data: ChatToggleData) => Promise<void>;
}

export const ChatsTable: React.FC<ChatsTableProps> = ({
  chats,
  isLoading = false,
  onChatToggle,
}) => {
  const navigate = useNavigate();

  // Optimistic overrides for the Enabled switch, keyed by chat id. An entry
  // exists only while a toggle mutation is in flight; it is reverted on error
  // (the toast comes from useChatToggle/useUpdateChatEnabled).
  const [optimisticEnabled, setOptimisticEnabled] = useState<
    Record<string, boolean>
  >({});

  // Column order mirrors the legacy admin chats table so Ops don't have to
  // re-learn the layout: Buyer · Buyer users · Distributor · Distributor
  // users · Chat name · Restaurant name · Customer number · Delivery address
  // · Enabled. Every column is always rendered — the table scrolls sideways
  // on narrow screens rather than dropping columns.
  const loadingTableColumns: LoadingTableColumn[] = [
    { label: 'Buyer', className: 'pl-6' },
    { label: 'Buyer users' },
    { label: 'Distributor' },
    { label: 'Distributor users' },
    { label: 'Chat name' },
    { label: 'Restaurant name' },
    { label: 'Customer number' },
    { label: 'Delivery address' },
    { label: 'Enabled' },
  ];

  const handleRowClick = (chatId: string) => {
    navigate(`/chats/${chatId}`);
  };

  // Buyer/distributor/user links sit inside the clickable row. Without this the
  // row's onClick fires on the same click and its /chats/:id navigation
  // immediately overwrites the link's own navigation. Mirrors the Enabled
  // switch, which already stops propagation below.
  const stopRowNavigation: React.MouseEventHandler<HTMLAnchorElement> = (
    event
  ) => {
    event.stopPropagation();
  };

  const handleToggle = async (chat: ChatListItem, enabled: boolean) => {
    setOptimisticEnabled((prev) => ({ ...prev, [chat.id]: enabled }));
    try {
      await onChatToggle({ id: chat.id, enabled });
    } catch {
      // Revert the optimistic value; the error toast is raised by the hook.
    } finally {
      // On success the Apollo cache now carries the new value; on error the
      // original value should show again — either way the override is stale.
      setOptimisticEnabled((prev) => {
        const next = { ...prev };
        delete next[chat.id];
        return next;
      });
    }
  };

  const renderRow = (chat: ChatListItem) => (
    <TableRow
      key={chat.id}
      className="hover:bg-default-50 dark:hover:bg-default-100/50 transition-colors duration-200 cursor-pointer"
      onClick={() => handleRowClick(chat.id)}
    >
      <TableCell className="pl-6 align-top">
        {chat.buyerEntity ? (
          <LinkCell
            to={`/buyers/${chat.buyerEntity.id}`}
            onClick={stopRowNavigation}
            className="text-sm block max-w-[11rem] break-words"
          >
            {chat.buyerEntity.name || chat.buyerEntity.id}
          </LinkCell>
        ) : (
          <span className="text-default-500">-</span>
        )}
      </TableCell>
      <TableCell className="align-top">
        <div className="space-y-1 max-w-[11rem]">
          {chat.buyerUsers.length > 0 ? (
            chat.buyerUsers.map((user) => (
              <div key={user.id} className="text-sm">
                <LinkCell
                  to={`/users/${user.id}`}
                  onClick={stopRowNavigation}
                  className="text-sm block break-words"
                >
                  {user.name}
                </LinkCell>
              </div>
            ))
          ) : (
            <span className="text-default-500">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        {chat.supplierEntity ? (
          <LinkCell
            to={`/suppliers/${chat.supplierEntity.id}`}
            onClick={stopRowNavigation}
            className="text-sm block max-w-[11rem] break-words"
          >
            {chat.supplierEntity.name || chat.supplierEntity.id}
          </LinkCell>
        ) : (
          <span className="text-default-500">-</span>
        )}
      </TableCell>
      <TableCell className="align-top">
        <div className="space-y-1 max-w-[11rem]">
          {chat.supplierUsers.length > 0 ? (
            chat.supplierUsers.map((user) => (
              <div key={user.id} className="text-sm">
                <LinkCell
                  to={`/users/${user.id}`}
                  onClick={stopRowNavigation}
                  className="text-sm block break-words"
                >
                  {user.name}
                </LinkCell>
              </div>
            ))
          ) : (
            <span className="text-default-500">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="max-w-[14rem] whitespace-normal break-words">
          {chat.chatName || <span className="text-default-500">-</span>}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="max-w-[10rem] break-words">
          {chat.restaurantName || <span className="text-default-500">-</span>}
        </div>
      </TableCell>
      <TableCell className="align-top whitespace-nowrap">
        {chat.customerNumber || '-'}
      </TableCell>
      <TableCell className="align-top">
        <div className="max-w-[12rem] whitespace-normal break-words">
          {chat.address || '-'}
        </div>
      </TableCell>
      <TableCell className="pr-6 align-top">
        {/* Stop propagation so toggling doesn't also open the chat row */}
        <div onClick={(event) => event.stopPropagation()}>
          <Switch
            size="sm"
            aria-label={`${
              (optimisticEnabled[chat.id] ?? chat.isEnabled)
                ? 'Disable'
                : 'Enable'
            } chat ${chat.chatName || chat.restaurantName || chat.id}`}
            isSelected={optimisticEnabled[chat.id] ?? chat.isEnabled}
            isDisabled={chat.id in optimisticEnabled}
            onValueChange={(enabled) => {
              handleToggle(chat, enabled);
            }}
          />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={loadingTableColumns}
      ariaLabel="Chats table"
      className="mb-6"
      isLoading={isLoading}
      showLoadingText={false}
      isEmpty={chats.length === 0}
      data={[...chats]}
      renderRow={(chat) => renderRow(chat as ChatListItem)}
      emptyState={{
        icon: FiMessageCircle,
        iconBgClass: 'bg-primary-50',
        iconColorClass: 'text-primary-500',
        title: 'No chats found',
        description: 'Try adjusting your search or filters.',
      }}
    />
  );
};
